export const STYLES = {
  g: {
    name: "Go For It",
    tagline: "Decisive · Competitive · Fast-Paced",
    strength:
      "You come alive when there's a challenge, a score, or a win on the line. Pressure doesn't shrink you — it sharpens you. You decide fast, commit, and move, which makes you dangerous in the moments other players are still thinking.",
    shadow:
      "The same engine can run hot: forcing momentum when patience would score better, or reading slower, deliberate partners as obstacles instead of information.",
  },
  o: {
    name: "Organize to Optimize",
    tagline: "Structured · Precise · Process-Driven",
    strength:
      "You perform best inside a clear plan: routine, data, measurable feedback. Precision and consistency are your home turf, and steady, trackable improvement keeps you energized season after season.",
    shadow:
      "Under pressure the process can become the trap — double-checking, overanalyzing, waiting for certainty while the moment passes.",
  },
  l: {
    name: "Light It Up",
    tagline: "Expressive · Energetic · People-Driven",
    strength:
      "You feed on atmosphere — people, conversation, a gallery, a little recognition. You raise the energy of every group you're in, and when the environment is lively your best golf shows up with it.",
    shadow:
      "When pressure hits, the energy can scatter: talking more, chasing the exciting option, losing the thread of a plan that was working.",
  },
  f: {
    name: "Feel It",
    tagline: "Intuitive · Rhythmic · Attuned",
    strength:
      "You play by feel — rhythm, timing, instinct. You notice shifts in mood, tempo and conditions before anyone else, and you remember the feel of a great shot long after the technical details fade.",
    shadow:
      "Rushed, judged or out of sync, you can withdraw and turn self-critical — and your natural rhythm goes quiet right when you need it.",
  },
};

export const OPPOSITE = { g: "f", o: "l", l: "o", f: "g" };

export const MULLIGAN_TIPS = {
  g: "Step back. Feel the situation. Reset before you push — ten seconds of calm beats a forced swing.",
  o: "Lighten up. Engage the people around you and let some energy back in before the next shot.",
  l: "Slow down. Organize the details — one target, one plan, one shot at a time.",
  f: "Take control. Decide fast, commit, and act — momentum rebuilds rhythm.",
};

export const LIKERT = [
  { d: "g", q: "I feel most engaged when there is a clear challenge, score, goal, or win on the line." },
  { d: "o", q: "I perform best when I have a clear plan, routine, or process to follow." },
  { d: "l", q: "I draw energy from people, conversation, competition, and the atmosphere around me." },
  { d: "f", q: "I perform best when I can trust feel, rhythm, timing, and instinct / intuition." },
  { d: "g", q: "When pressure rises, I prefer to make a decision quickly and move into action." },
  { d: "o", q: "I like to think through options carefully before committing to action." },
  { d: "l", q: "I often perform better when the environment is lively, social, or interactive." },
  { d: "f", q: "I notice subtle changes in mood, tempo, energy, or environment before others do." },
  { d: "g", q: "I would rather take a bold line and live with the result than play too cautiously." },
  { d: "o", q: "Data, details, checklists, or measurable feedback help me feel confident." },
  { d: "l", q: "I enjoy being the person who keeps the group energized, loose, or engaged." },
  { d: "f", q: "I like having enough space to sense what feels right before committing." },
  { d: "g", q: "I perform best when I have freedom to act without too much discussion or delay." },
  { d: "o", q: "Precision and consistency matter more to me than speed." },
  { d: "l", q: "Recognition, visible feedback, or an audience can raise my level of performance." },
  { d: "f", q: "Supportive energy, calm surroundings, and emotional safety help me perform at my best." },
  { d: "g", q: "Competition sharpens my focus and brings out another gear in me." },
  { d: "o", q: "I am energized by steady improvement and knowing exactly what to work on next." },
  { d: "l", q: "I like room for spontaneity, creativity, humor, and flexibility in how things are done." },
  { d: "f", q: "I often remember the feel of a good performance more clearly than the technical details." },
  { d: "g", q: "When things are not going well, my instinct is to push harder and try to force momentum." },
  { d: "o", q: "When pressure rises, I may slow down, double-check, or overanalyze before acting." },
  { d: "l", q: "When pressure rises, I may become scattered, talk more, or chase the exciting option." },
  { d: "f", q: "When pressure rises, I may withdraw, become self-critical, or lose my natural rhythm." },
  { d: "g", q: "I get frustrated when others slow the pace, overthink, or hesitate too long." },
  { d: "o", q: "I get frustrated when people ignore structure, rules, preparation, or important details." },
  { d: "l", q: "I get frustrated when the environment feels flat, overly serious, rigid, or low energy." },
  { d: "f", q: "I get frustrated when I feel rushed, pressured, judged, or emotionally out of sync." },
];

export const CLARIFIERS = [
  {
    q: "Under pressure, which feels more natural to you?",
    options: [
      { key: "g", label: "Take control and act quickly" },
      { key: "o", label: "Slow down and organize the details" },
      { key: "l", label: "Engage others and build energy" },
      { key: "f", label: "Step back, feel the situation, and reset" },
    ],
  },
  {
    q: "When you are at your absolute best, people would most likely describe you as:",
    options: [
      { key: "g", label: "Decisive and driven" },
      { key: "o", label: "Prepared and reliable" },
      { key: "l", label: "Energizing and expressive" },
      { key: "f", label: "Calm and intuitive" },
    ],
  },
];

export const LIKERT_OPTIONS = [
  { value: 1, label: "Strongly Disagree", short: "SD" },
  { value: 2, label: "Disagree", short: "D" },
  { value: 3, label: "Sometimes", short: "N" },
  { value: 4, label: "Agree", short: "A" },
  { value: 5, label: "Strongly Agree", short: "SA" },
];

export function computeResult(answers) {
  const raw = { g: 0, o: 0, l: 0, f: 0 };
  LIKERT.forEach((item, i) => {
    raw[item.d] += answers[i] || 0;
  });
  const clarifierVotes = { g: 0, o: 0, l: 0, f: 0 };
  [28, 29].forEach((i) => {
    if (answers[i]) clarifierVotes[answers[i]] += 1;
  });
  const sorted = Object.entries(raw).sort(
    (a, b) => b[1] - a[1] || clarifierVotes[b[0]] - clarifierVotes[a[0]]
  );
  const primary = sorted[0][0];
  const accent = sorted[1][0];
  const top = sorted[0][1];
  const closeCount = sorted.filter(([, v]) => top - v <= 2).length;
  const adaptive = closeCount >= 3;
  const blend = !adaptive && sorted[0][1] - sorted[1][1] <= 2;
  const scores = {};
  Object.entries(raw).forEach(([k, v]) => {
    scores[k] = Math.round((v / 35) * 100);
  });
  return { scores, raw, primary, accent, blend, adaptive, mulligan: OPPOSITE[primary] };
}
